import React, { useState, useRef } from "react";

const ICON_SIZE = 48;
const MAX_ICONS = 1;

function FieldTooltip({ text }) {
  return (
    <div className="tooltip">
      <span className="help-icon">?</span>
      <span className="tooltiptext">{text}</span>
    </div>
  );
}

function FieldLabel({ label, tooltip }) {
  return (
    <div className="field-label-container">
      <div className="field-title">
        <strong>{label}</strong>
      </div>
      {tooltip ? <FieldTooltip text={tooltip} /> : null}
    </div>
  );
}

export default function AgentForm({
  mode, // "create" | "edit"
  value,
  onPatch,
  onPickIconFile,
  iconUrls = [], // массив относительных URL для edit (макс 1)
  iconPreviewUrl,
  toAbsoluteIconUrl,
  iconUploading,
  iconError,
  onDeleteIcon,
  onClearIconPreview,
  onSubmit,
  onCancel,
  t,
  isGeneratingFaq,
  isGeneratingLinks,
  onGenerateFaq,
  onGenerateTrainingLinks,
  className,
}) {
  const isCreate = mode === "create";
  const domainTrimmed = (value?.domain || "").trim();
  const [deleteConfirmIcon, setDeleteConfirmIcon] = useState(null);
  const fileInputRef = useRef(null);

  const hasIcon = isCreate ? !!iconPreviewUrl : iconUrls.length > 0;
  const canAdd = !hasIcon;
  const toAbs = toAbsoluteIconUrl || ((u) => u || "");

  return (
    <div className={className}>
      <FieldLabel
        label={isCreate ? t("Agent Name *") : t("Agent Name")}
        tooltip={t("Display name of your AI agent (e.g. Support Bot).")}
      />
      <input
        type="text"
        placeholder={t("Customer Support, Sales Assistant")}
        value={value?.name || ""}
        onChange={(e) => onPatch({ name: e.target.value })}
        className={isCreate && !(value?.name || "").trim() ? "input-error" : ""}
      />

      <FieldLabel
        label={isCreate ? t("Domain or IP *") : t("Domain or IP")}
        tooltip={t("Domain or IP of the site where the widget will be embedded (e.g. example.com).")}
      />
      <input
        type="text"
        placeholder={t("example.com or 192.168.1.1")}
        value={value?.domain || ""}
        onChange={(e) => onPatch({ domain: e.target.value })}
        className={isCreate && !domainTrimmed ? "input-error" : ""}
        required={isCreate}
      />

      <FieldLabel
        label={isCreate ? t("Prompt *") : t("Prompt")}
        tooltip={t("Choose the communication style: Formal (professional) or Friendly (casual and warm).")}
      />
      <select
        value={value?.prompt || ""}
        onChange={(e) => onPatch({ prompt: e.target.value })}
        className={isCreate && !value?.prompt ? "input-error" : ""}
      >
        <option value="">{isCreate ? t("Select Prompt *") : t("Select Prompt")}</option>
        <option value="formal">
          {t("Formal - Professional, business-like communication style")}
        </option>
        <option value="friendly">
          {t("Friendly - Casual, warm and approachable communication style")}
        </option>
      </select>

      <FieldLabel
        label={t("Welcome Message")}
        tooltip={t("First message the user sees when opening the chat.")}
      />
      <textarea
        placeholder={t("Hi! How can I help you today?")}
        value={value?.welcome_message || ""}
        onChange={(e) => onPatch({ welcome_message: e.target.value })}
      />

      <FieldLabel
        label={t("Contact Details")}
        tooltip={t("Email, phone, Telegram, etc. Shown when the user asks for contact.")}
      />
      <textarea
        placeholder={t("support@example.com, +1 234 567 8900, @telegram")}
        value={value?.contact_details || ""}
        onChange={(e) => onPatch({ contact_details: e.target.value })}
      />

      <FieldLabel
        label={t("Scenario")}
        tooltip={t("Context and rules for the AI (knowledge base, behavior).")}
      />
      <textarea
        placeholder={t("Product info, policies, behavior rules for the AI...")}
        value={value?.knowledge_base || ""}
        onChange={(e) => onPatch({ knowledge_base: e.target.value })}
      />

      <FieldLabel
        label={t("Training Links")}
        tooltip={t("URLs the AI can use to answer questions (one per line).")}
      />
      <textarea
        placeholder={t("https://example.com/page1 (one URL per line)")}
        value={value?.training_links || ""}
        onChange={(e) => onPatch({ training_links: e.target.value })}
      />
      <div className="agent-form-generate-wrap">
        <p className="agent-form-generate-hint">
          {t("You can use automatic generation for this field, but after generation, please verify the correctness of the content.")}
        </p>
        <button
          type="button"
          title={!domainTrimmed ? t("To generate Training Links, enter the domain where the agent will be placed") : undefined}
          onClick={!domainTrimmed ? undefined : onGenerateTrainingLinks}
          disabled={!domainTrimmed || isGeneratingLinks}
          className={`agent-form-generate-btn ${
            isGeneratingLinks ? "generating" : domainTrimmed ? "active" : "inactive"
          }`}
        >
          {isGeneratingLinks ? t("Generating...") : t("Generate")}
        </button>
      </div>

      <FieldLabel
        label={t("FAQ")}
        tooltip={t("Frequently asked questions and answers for the AI (one Q&A per block).")}
      />
      <textarea
        placeholder={t("How do I register?:Through the registration form.")}
        value={value?.faq || ""}
        onChange={(e) => onPatch({ faq: e.target.value })}
        rows={6}
      />
      <div className="agent-form-generate-wrap">
        <p className="agent-form-generate-hint">
          {t("You can use automatic generation for this field, but after generation, please verify the correctness of the content.")}
        </p>
        <button
          type="button"
          title={!domainTrimmed ? t("To generate FAQ, enter the domain where the agent will be placed") : undefined}
          onClick={!domainTrimmed ? undefined : onGenerateFaq}
          disabled={!domainTrimmed || isGeneratingFaq}
          className={`agent-form-generate-btn ${
            isGeneratingFaq ? "generating" : domainTrimmed ? "active" : "inactive"
          }`}
        >
          {isGeneratingFaq ? t("Generating...") : t("Generate")}
        </button>
      </div>

      <FieldLabel
        label={t("Widget Icon (optional)")}
        tooltip={t("One icon for the chat widget. Click icon to remove. Delete to add another.")}
      />
      <div className="widget-icon-row" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        {isCreate && iconPreviewUrl && (
          <div
            className="widget-icon-circle widget-icon-circle--preview"
            style={{
              width: ICON_SIZE,
              height: ICON_SIZE,
              borderRadius: "50%",
              overflow: "hidden",
              background: "#fff",
              flexShrink: 0,
              cursor: "pointer",
              position: "relative",
            }}
            onClick={() => onClearIconPreview?.()}
            title={t("Click to remove")}
          >
            <img
              src={iconPreviewUrl}
              alt=""
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        )}
        {!isCreate && iconUrls.map((relUrl) => (
          <div
            key={relUrl}
            className="widget-icon-circle-wrapper"
            style={{ flexShrink: 0 }}
          >
            <div
              className="widget-icon-circle"
              style={{
                width: ICON_SIZE,
                height: ICON_SIZE,
                borderRadius: "50%",
                overflow: "hidden",
                background: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setDeleteConfirmIcon(relUrl)}
              title={t("Click to delete")}
            >
              <img
                src={toAbs(relUrl)}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>
          </div>
        ))}
        {canAdd && (
            <button
              type="button"
              className="widget-icon-add"
            style={{
              width: ICON_SIZE,
              height: ICON_SIZE,
              borderRadius: "50%",
              border: "1px dashed #adb5bd",
              background: "#f8f9fa",
              color: "#6c757d",
              fontSize: 22,
              cursor: iconUploading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            onClick={() => !iconUploading && fileInputRef.current?.click()}
            disabled={!!iconUploading}
            title={t("Add icon")}
          >
            +
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
              e.target.value = "";
              alert(t("File is too large. Max size is 10MB."));
              return;
            }
            onPickIconFile?.(file);
            e.target.value = "";
          }}
        />
        {iconUploading ? (
          <span style={{ color: "#6c757d", fontSize: 13 }}>{t("Uploading...")}</span>
        ) : null}
        {iconError ? (
          <span style={{ color: "#dc3545", fontSize: 13 }}>{iconError}</span>
        ) : null}
      </div>
      {deleteConfirmIcon && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setDeleteConfirmIcon(null)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              maxWidth: 320,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <p style={{ margin: "0 0 16px", fontSize: 15 }}>{t("Delete this icon?")}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteConfirmIcon(null)}>{t("Cancel")}</button>
              <button
                className="delete-button"
                onClick={() => {
                  onDeleteIcon?.(deleteConfirmIcon);
                  setDeleteConfirmIcon(null);
                }}
              >
                {t("Delete")}
              </button>
            </div>
          </div>
        </div>
      )}

      <FieldLabel
        label={t("Widget Color")}
        tooltip={t("Accent color of the chat button and widget (hex, e.g. #667eea).")}
      />
      <div className="widget-color-row">
        <input
          type="text"
          value={value?.widget_color || "#667eea"}
          onChange={(e) => onPatch({ widget_color: e.target.value || "#667eea" })}
          className="widget-color-hex"
          placeholder="#667eea"
        />
        <div className="widget-color-picker-wrap">
          <button
            type="button"
            title={t("Pick color")}
            className="widget-color-picker-btn"
            style={{
              background: value?.widget_color || "#667eea",
              color: (() => {
                const hex = (value?.widget_color || "#667eea").replace(/^#/, "");
                if (hex.length !== 6) return "#fff";
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                const y = 0.299 * r + 0.587 * g + 0.114 * b;
                return y > 186 ? "#1a1a1a" : "#fff";
              })(),
            }}
          >
            🎨
          </button>
          <input
            type="color"
            title={t("Pick color")}
            value={(() => {
              const v = value?.widget_color || "#667eea";
              if (/^#[a-fA-F0-9]{6}$/.test(v)) return v;
              if (/^[a-fA-F0-9]{6}$/.test(v)) return `#${v}`;
              return "#667eea";
            })()}
            onChange={(e) => onPatch({ widget_color: e.target.value || "#667eea" })}
            className="widget-color-picker-input"
          />
        </div>
        <button
          type="button"
          className="widget-color-reset"
          title={t("Reset to default")}
          onClick={() => onPatch({ widget_color: "#667eea" })}
        >
          ↺
        </button>
      </div>

      <FieldLabel
        label={t("Use support style for admin messages")}
        tooltip={t("Show admin replies in a distinct support-agent style.")}
      />
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={!!value?.use_support_style}
          onChange={(e) => onPatch({ use_support_style: e.target.checked })}
          className="form-checkbox"
        />
        <span>{t("Use support style for admin messages")}</span>
      </label>

      {!!value?.use_support_style && (
        <>
          <FieldLabel
          label={t("Support Name")}
          tooltip={t("Name shown for support-style messages in the chat.")}
        />
          <input
            type="text"
            placeholder={t("Support or John")}
            value={value?.support_name || "Support"}
            onChange={(e) => onPatch({ support_name: e.target.value })}
            className="agent-form-input-support-name"
          />
        </>
      )}

      <FieldLabel
        label={t("Enable auto-open")}
        tooltip={t("Automatically open the chat after a delay (seconds).")}
      />
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={!!value?.auto_open_enabled}
          onChange={(e) => onPatch({ auto_open_enabled: e.target.checked })}
          className="form-checkbox"
        />
        <span>{t("Enable auto-open")}</span>
      </label>
      {!!value?.auto_open_enabled && (
        <input
          type="number"
          min={0}
          placeholder={t("5")}
          value={value?.auto_open_delay || 0}
          onChange={(e) =>
            onPatch({ auto_open_delay: parseInt(e.target.value, 10) || 0 })
          }
          className="agent-form-input-auto-open"
        />
      )}

      <FieldLabel
        label={t("Enable auto-archive")}
        tooltip={t("Automatically archive the chat after inactivity (seconds).")}
      />
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={!!value?.auto_archive_enabled}
          onChange={(e) => onPatch({ auto_archive_enabled: e.target.checked })}
          className="form-checkbox"
        />
        <span>{t("Enable auto-archive")}</span>
      </label>
      {!!value?.auto_archive_enabled && (
        <input
          type="number"
          min={0}
          placeholder={t("3600")}
          value={value?.auto_archive_delay || 0}
          onChange={(e) =>
            onPatch({ auto_archive_delay: parseInt(e.target.value, 10) || 0 })
          }
        />
      )}

      <FieldLabel
        label={t("Enable sound notification in widget")}
        tooltip={t("Play a sound when a new message arrives in the widget.")}
      />
      <label className="checkbox-label">
        <input
          type="checkbox"
          checked={typeof value?.sound_enabled === "boolean" ? value.sound_enabled : true}
          onChange={(e) => onPatch({ sound_enabled: e.target.checked })}
          className="form-checkbox"
        />
        <span>{t("Enable sound notification in widget")}</span>
      </label>

      <div className="agent-form-buttons">
        <button onClick={onSubmit}>{isCreate ? t("Add") : t("Save")}</button>
        <button onClick={onCancel}>{t("Cancel")}</button>
      </div>
    </div>
  );
}

